import React, { useState } from "react";
import styled from "styled-components";
import { Tooltip } from "antd";
import dayjs from "dayjs";
import { Avatar } from "../../visuals/Avatar";
import { CompanyNewsItem } from "../../../models/newsModels";
import { NewsSourceType } from "../../../enums/News";
import { greyText, lightGreyOutline, lightGreyText } from "../../../styles";
import { formatReadable } from "../../../utils/formattingUtils";
import { NewsTags } from "./NewsTags";
import { DIRECTION_COLORS, RINGED_DIRECTIONS } from "./constants";
import { formatNewsDay, toSentenceLabel } from "./utils";

type NewsTimelineRowProps = {
  item: CompanyNewsItem;
  showDate: boolean;
  compact: boolean;
};

const RowLink = styled.a<{ $compact: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: ${({ $compact }): string =>
    $compact ? "36px 12px minmax(0, 1fr)" : "44px 16px minmax(0, 1fr)"};
  align-items: start;
  padding: ${({ $compact }): string =>
    $compact ? "5px 6px 5px 0" : "8px 6px 8px 0"};
  border-radius: 6px;
  text-decoration: none;
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 6px;
    background-color: ${lightGreyOutline};
    opacity: 0;
    transition: opacity 0.15s ease;
  }
  &:hover::before,
  &:focus-visible::before {
    opacity: 0.35;
  }
  & > * {
    position: relative;
  }
`;

const DateGutter = styled.span`
  padding-top: 2px;
  font-family: "ApercuMono", "Apercu", monospace;
  font-size: 10.5px;
  color: ${greyText};
  white-space: nowrap;
`;

const Dot = styled.span<{ $color: string; $ringed: boolean }>`
  justify-self: center;
  margin-top: 6px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: ${({ $color }): string => $color};
  box-shadow: 0 0 0 2px white;
  outline: ${({ $ringed, $color }): string =>
    $ringed ? `1px solid ${$color}` : "none"};
  outline-offset: 2px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  padding-left: 6px;
`;

const HeadlineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const Headline = styled.span`
  display: -webkit-box;
  flex: 1;
  min-width: 0;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-family: "Apercu";
  font-size: 13px;
  line-height: 18px;
  color: ${greyText};
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`;

const TagsHoverZone = styled.div`
  min-width: 0;
`;

const Publisher = styled.span`
  flex-shrink: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: "Apercu";
  font-size: 11px;
  color: ${lightGreyText};
  white-space: nowrap;
`;

const SOURCE_LABELS: Record<NewsSourceType, string> = {
  [NewsSourceType.NEWS_ARTICLE]: "News",
  [NewsSourceType.PRESS_RELEASE]: "Press release",
};

const RowTooltip = ({ item }: { item: CompanyNewsItem }): JSX.Element => (
  <>
    <div>{item.title}</div>
    <div>
      {[
        item.publisher,
        SOURCE_LABELS[item.source_type],
        dayjs(item.published_at).format("D MMM YYYY, HH:mm"),
      ].join(" · ")}
    </div>
    {item.company_dimensions.length > 0 && (
      <div>
        Dimensions: {item.company_dimensions.map(toSentenceLabel).join(", ")}
      </div>
    )}
    {item.financial_channels.length > 0 && (
      <div>
        Financial channels:{" "}
        {item.financial_channels.map(toSentenceLabel).join(", ")}
      </div>
    )}
  </>
);

export const NewsTimelineRow = ({
  item,
  showDate,
  compact,
}: NewsTimelineRowProps): JSX.Element => {
  const [rowTooltipOpen, setRowTooltipOpen] = useState(false);
  const [isOverTags, setIsOverTags] = useState(false);
  return (
    <Tooltip
      title={<RowTooltip item={item} />}
      placement={compact ? "topLeft" : "left"}
      mouseEnterDelay={0.4}
      open={rowTooltipOpen && !isOverTags}
      onOpenChange={setRowTooltipOpen}
    >
      <RowLink
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        $compact={compact}
      >
        <DateGutter>
          {showDate ? formatNewsDay(item.published_at) : ""}
        </DateGutter>
        <Dot
          $color={DIRECTION_COLORS[item.evidence_direction]}
          $ringed={RINGED_DIRECTIONS.includes(item.evidence_direction)}
        />
        <Body>
          <HeadlineRow>
            <Avatar
              username={formatReadable(item.company_name)}
              image={item.company_image}
              size="xsmall"
            />
            <Headline>{item.title}</Headline>
          </HeadlineRow>
          {!compact && (
            <MetaRow>
              <TagsHoverZone
                onMouseEnter={(): void => setIsOverTags(true)}
                onMouseLeave={(): void => setIsOverTags(false)}
              >
                <NewsTags item={item} />
              </TagsHoverZone>
              <Publisher>{item.publisher}</Publisher>
            </MetaRow>
          )}
        </Body>
      </RowLink>
    </Tooltip>
  );
};
